/**
 * Platform detection utilities that avoid brittle user-agent sniffing
 * Uses feature detection and modern platform APIs for robust platform identification
 *
 * Benefits over user-agent sniffing:
 * - ✅ Works with Chrome on iOS, edge cases, and future platforms
 * - ✅ Not fooled by user-agent spoofing or edge cases
 * - ✅ Uses modern navigator.userAgentData when available
 * - ✅ Graceful fallbacks for unknown platforms
 * - ✅ Configurable preferences for different map apps
 * - ✅ Easy to extend with new platforms and map services
 *
 * Replaces brittle if/else chains with maintainable lookup tables.
 */

export type PlatformType = 'ios' | 'android' | 'desktop' | 'unknown';

export interface MapUrlConfig {
  type: 'native' | 'web';
  url: string;
  name: string;
}

/**
 * Detect platform using feature detection and modern APIs, avoiding user-agent sniffing
 */
export function detectPlatform(): PlatformType {
  // Use modern platform detection APIs when available
  if ('userAgentData' in navigator) {
    const nav = navigator as any;
    if (nav.userAgentData?.platform) {
      const platform = nav.userAgentData.platform.toLowerCase();
      if (platform.includes('android')) return 'android';
      if (
        platform.includes('ios') ||
        platform.includes('iphone') ||
        platform.includes('ipad')
      )
        return 'ios';
      return 'desktop';
    }
  }

  // Fallback to feature detection and minimal UA checks as last resort
  // Check for mobile-specific APIs and features
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const hasOrientationChange = 'onorientationchange' in window;
  const hasDeviceMotion = 'DeviceMotionEvent' in window;

  // iOS-specific feature detection
  const hasIOSFeatures =
    hasTouch &&
    hasOrientationChange &&
    (window.screen.width === 375 ||
      window.screen.width === 414 || // Common iOS widths
      window.screen.width === 768 ||
      window.screen.width === 1024); // iPad widths

  // Android-specific feature detection
  const hasAndroidFeatures =
    hasTouch &&
    hasDeviceMotion &&
    'chrome' in window && // Most Android browsers are Chrome-based
    !hasIOSFeatures;

  // Final fallback to minimal UA check if feature detection is inconclusive
  if (!hasIOSFeatures && !hasAndroidFeatures && hasTouch) {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('android')) return 'android';
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod'))
      return 'ios';
  }

  if (hasIOSFeatures) return 'ios';
  if (hasAndroidFeatures) return 'android';
  if (!hasTouch) return 'desktop';

  return 'unknown';
}

/**
 * Map URL configurations for different platforms
 * Provides fallbacks and multiple options per platform
 */
const MAP_URL_CONFIGS: Record<PlatformType, MapUrlConfig[]> = {
  ios: [
    {
      type: 'native',
      url: 'maps://maps.apple.com/?q={lat},{lng}&t=m',
      name: 'Apple Maps (native)',
    },
    {
      type: 'web',
      url: 'https://maps.apple.com/?q={lat},{lng}&t=m',
      name: 'Apple Maps (web)',
    },
    {
      type: 'web',
      url: 'https://www.google.com/maps?q={lat},{lng}',
      name: 'Google Maps',
    },
  ],
  android: [
    {
      type: 'native',
      url: 'geo:{lat},{lng}?q={lat},{lng}',
      name: 'Android Maps (native)',
    },
    {
      type: 'web',
      url: 'https://www.google.com/maps?q={lat},{lng}',
      name: 'Google Maps',
    },
  ],
  desktop: [
    {
      type: 'web',
      url: 'https://www.google.com/maps?q={lat},{lng}',
      name: 'Google Maps',
    },
    {
      type: 'web',
      url: 'https://maps.apple.com/?q={lat},{lng}&t=m',
      name: 'Apple Maps',
    },
  ],
  unknown: [
    {
      type: 'web',
      url: 'https://www.google.com/maps?q={lat},{lng}',
      name: 'Google Maps',
    },
  ],
};

/**
 * Generate a map URL for the current platform with proper fallbacks
 */
export function createPlatformAwareMapUrl(
  latitude: string | number,
  longitude: string | number,
  options: {
    preferNative?: boolean;
    preferredApp?: 'google' | 'apple' | 'native';
  } = {}
): string {
  const { preferNative = true, preferredApp = 'native' } = options;
  const platform = detectPlatform();
  const configs = MAP_URL_CONFIGS[platform];

  // Filter configs based on preferences
  let filteredConfigs = configs;

  if (preferNative) {
    const nativeConfigs = configs.filter(config => config.type === 'native');
    if (nativeConfigs.length > 0) {
      filteredConfigs = nativeConfigs;
    }
  }

  // Apply app preference
  if (preferredApp !== 'native') {
    const appConfigs = configs.filter(config =>
      config.name.toLowerCase().includes(preferredApp)
    );
    if (appConfigs.length > 0) {
      filteredConfigs = appConfigs;
    }
  }

  // Use the first matching config (they're ordered by preference)
  const selectedConfig = filteredConfigs[0];

  // Replace placeholders with actual coordinates
  return selectedConfig.url
    .replace('{lat}', String(latitude))
    .replace('{lng}', String(longitude));
}

/**
 * Get available map options for the current platform
 */
export function getAvailableMapOptions(): MapUrlConfig[] {
  const platform = detectPlatform();
  return MAP_URL_CONFIGS[platform];
}

/**
 * Check if native map apps are likely available
 */
export function hasNativeMapSupport(): boolean {
  const platform = detectPlatform();
  return MAP_URL_CONFIGS[platform].some(config => config.type === 'native');
}
